import { createContext, useContext, useState, useCallback } from "react";

export const translations = {
  fr: {
    nav: {
      bridge: "Le Pont",
      directions: "Directions",
      stakeholders: "Interlocuteurs",
      method: "Méthode",
      sectors: "Secteurs",
      confidentiality: "Confidentialité",
      opportunities: "Opportunités",
      contact: "Contact",
      admin: "Espace admin",
    },
    hero: {
      overline: "Intermédiation · Études · Intelligence financière",
      line1: "Relier les ambitions",
      line2: "africaines",
      line3: "aux opportunités mondiales.",
      sub1: "Bridge Intell crée des passerelles entre les gouvernements africains et les acteurs économiques, financiers, industriels et institutionnels internationaux.",
      sub2: "Grâce à l'intermédiation, aux études et à l'intelligence financière, nous transformons les besoins, les projets et les opportunités en connexions stratégiques.",
      ctaPrimary: "Présenter un projet",
      ctaSecondary: "Explorer une opportunité",
      hubs: "Abidjan · Dakar · Douala · Kigali — Paris · Londres · Dubaï · Washington · Singapour · New York",
      scroll: "Découvrir",
    },
    bridge: {
      overline: "Le Pont",
      title: "Un pont entre deux univers",
      text: "Bridge Intell connecte les gouvernements africains à un écosystème international d'investisseurs, d'institutions et de partenaires — et inversement.",
      left: "Gouvernements & Institutions africaines",
      center: "Bridge Intell",
      right: "Écosystème international",
      pills: ["Investisseurs", "Fonds", "Banques", "Bailleurs", "Entreprises", "Industriels", "Institutions", "Experts"],
      africaToWorld: "Afrique → Monde",
      worldToAfrica: "Monde → Afrique",
    },
    directions: {
      overline: "Deux directions, une même mission",
      d1: {
        num: "01",
        label: "Afrique → Monde",
        title: "Transformer les besoins stratégiques africains en opportunités internationales structurées.",
        services: [
          "Recherche d'investisseurs",
          "Recherche de financements",
          "Recherche de partenaires",
          "Identification de technologies",
          "Identification d'expertise",
          "Valorisation de projets",
          "Structuration d'opportunités",
          "Mise en relation internationale",
        ],
      },
      d2: {
        num: "02",
        label: "Monde → Afrique",
        title: "Donner aux acteurs internationaux les clés pour comprendre, accéder et agir sur les marchés africains.",
        services: [
          "Intelligence pays",
          "Études sectorielles",
          "Identification d'opportunités",
          "Analyse de marché",
          "Identification de partenaires",
          "Cartographie institutionnelle",
          "Due diligence",
          "Mise en relation institutionnelle",
          "Accompagnement stratégique",
        ],
      },
    },
    stakeholders: {
      overline: "Nos interlocuteurs",
      title: "Des deux côtés du pont",
      leftTitle: "Côté Afrique",
      rightTitle: "Côté international",
      left: [
        "Présidences et gouvernements",
        "Ministères",
        "Administrations publiques",
        "Agences gouvernementales",
        "Entreprises publiques",
        "Collectivités",
        "Institutions financières publiques",
        "Structures de promotion des investissements",
      ],
      right: [
        "Investisseurs institutionnels",
        "Fonds d'investissement",
        "Banques",
        "Institutions financières",
        "Bailleurs de fonds",
        "Entreprises multinationales",
        "Industriels",
        "Cabinets spécialisés",
        "Entreprises technologiques",
        "Partenaires au développement",
      ],
    },
    process: {
      overline: "Intelligence avant intermédiation",
      title: "Une connexion n'a de valeur que si elle est pertinente.",
      text: "Chaque mission d'intermédiation repose sur un travail préalable de compréhension, d'analyse et de qualification. Bridge Intell analyse le contexte, les acteurs, les enjeux, les risques et les intérêts respectifs avant d'organiser une mise en relation.",
      steps: [
        { name: "Comprendre", desc: "Le besoin, le contexte institutionnel et les objectifs réels de chaque partie." },
        { name: "Analyser", desc: "Les acteurs, les enjeux, les risques, les environnements économiques et réglementaires." },
        { name: "Qualifier", desc: "La solidité, la crédibilité et l'alignement des partenaires potentiels." },
        { name: "Connecter", desc: "Organiser la mise en relation, au bon niveau, au bon moment." },
        { name: "Accompagner", desc: "Suivre la relation jusqu'à la structuration et la décision." },
      ],
    },
    sectors: {
      overline: "Secteurs stratégiques",
      title: "Là où se jouent les projets structurants",
      text: "Quatorze secteurs prioritaires, de l'infrastructure aux ressources naturelles, où Bridge Intell mobilise son réseau et son intelligence de marché.",
    },
    confidentiality: {
      overline: "Confidentialité",
      title: "La discrétion fait partie de notre métier.",
      text: "Les projets institutionnels, financiers et stratégiques nécessitent un niveau élevé de confidentialité. Bridge Intell applique une approche fondée sur la discrétion, la maîtrise de l'information et la protection des intérêts des parties impliquées.",
      points: [
        "Aucun projet, gouvernement, investisseur ou mise en relation n'est rendu public sans autorisation.",
        "Circulation de l'information maîtrisée, au compte-gouttes et sur accord.",
        "Protection constante des intérêts des parties impliquées.",
      ],
      marquee: [
        "L'Afrique et le monde",
        "Les gouvernements et les investisseurs",
        "Les projets et les capitaux",
        "Les besoins et les solutions",
        "L'information et la décision",
        "Les opportunités et les partenaires",
      ],
    },
    opportunities: {
      overline: "Opportunités",
      title: "Une sélection d'opportunités qualifiées",
      text: "Projets d'infrastructure, recherches d'investisseurs, partenariats public-privé : chaque opportunité publiée est analysée et qualifiée au préalable. Trois niveaux de visibilité protègent l'information sensible.",
      all: "Toutes",
      public: "Public",
      confidential: "Confidentiel",
      private: "Strictement privé",
      locked: "Opportunité confidentielle",
      lockedText: "Les informations détaillées de cette opportunité sont accessibles sur inscription et après qualification.",
      requestAccess: "Demander l'accès",
      profileSought: "Profil recherché",
      amount: "Montant estimatif",
      statusOpen: "Ouverte",
      statusClosed: "Clôturée",
      privateNote: "Les opportunités strictement privées ne sont jamais publiées. Elles sont présentées uniquement à des parties qualifiées, sous engagement de confidentialité.",
    },
    contact: {
      overline: "Contact",
      title: "Ouvrir une passerelle",
      text: "Présentez un projet, explorez une opportunité ou demandez l'accès à un dossier confidentiel. Chaque message est traité avec discrétion.",
      intentProject: "Présenter un projet",
      intentOpportunity: "Explorer une opportunité",
      intentAccess: "Demander l'accès à un dossier",
      roleAfrica: "Institution africaine",
      roleWorld: "Acteur international",
      name: "Nom complet",
      organization: "Organisation",
      email: "Email professionnel",
      country: "Pays",
      sector: "Secteur concerné",
      message: "Votre message",
      messagePlaceholder: "Décrivez brièvement votre projet, votre besoin ou votre intérêt…",
      submit: "Envoyer en toute confidentialité",
      sending: "Envoi…",
      success: "Message transmis. Bridge Intell vous répondra avec la plus grande discrétion.",
      error: "Une erreur est survenue. Veuillez réessayer.",
    },
    footer: {
      signature: "Bridging Africa to the World",
      alt1: "L'Afrique connectée aux opportunités du monde",
      alt2: "Le pont stratégique entre l'Afrique et le monde",
      rights: "Tous droits réservés.",
    },
    admin: {
      login: "Espace administrateur",
      email: "Email",
      password: "Mot de passe",
      signIn: "Se connecter",
      logout: "Déconnexion",
      messages: "Messages",
      opportunities: "Opportunités",
      sectors: "Secteurs",
      newOpportunity: "Nouvelle opportunité",
      newSector: "Nouveau secteur",
      save: "Enregistrer",
      cancel: "Annuler",
      edit: "Modifier",
      delete: "Supprimer",
      markRead: "Marquer lu",
      noMessages: "Aucun message pour le moment.",
      visibility: "Visibilité",
    },
  },
  en: {
    nav: {
      bridge: "The Bridge",
      directions: "Directions",
      stakeholders: "Stakeholders",
      method: "Method",
      sectors: "Sectors",
      confidentiality: "Confidentiality",
      opportunities: "Opportunities",
      contact: "Contact",
      admin: "Admin area",
    },
    hero: {
      overline: "Intermediation · Advisory · Financial intelligence",
      line1: "Bridging African",
      line2: "ambitions",
      line3: "to global opportunities.",
      sub1: "Bridge Intell builds bridges between African governments and international economic, financial, industrial and institutional actors.",
      sub2: "Through intermediation, research and financial intelligence, we turn needs, projects and opportunities into strategic connections.",
      ctaPrimary: "Present a project",
      ctaSecondary: "Explore an opportunity",
      hubs: "Abidjan · Dakar · Douala · Kigali — Paris · London · Dubai · Washington · Singapore · New York",
      scroll: "Discover",
    },
    bridge: {
      overline: "The Bridge",
      title: "A bridge between two worlds",
      text: "Bridge Intell connects African governments to an international ecosystem of investors, institutions and partners — and vice versa.",
      left: "African Governments & Institutions",
      center: "Bridge Intell",
      right: "International Ecosystem",
      pills: ["Investors", "Funds", "Banks", "Donors", "Corporates", "Industrials", "Institutions", "Experts"],
      africaToWorld: "Africa → World",
      worldToAfrica: "World → Africa",
    },
    directions: {
      overline: "Two directions, one mission",
      d1: {
        num: "01",
        label: "Africa → World",
        title: "Turning Africa's strategic needs into structured international opportunities.",
        services: [
          "Investor search",
          "Financing search",
          "Partner search",
          "Technology identification",
          "Expertise identification",
          "Project valorisation",
          "Opportunity structuring",
          "International matchmaking",
        ],
      },
      d2: {
        num: "02",
        label: "World → Africa",
        title: "Giving international actors the keys to understand, access and act on African markets.",
        services: [
          "Country intelligence",
          "Sector studies",
          "Opportunity identification",
          "Market analysis",
          "Partner identification",
          "Institutional mapping",
          "Due diligence",
          "Institutional matchmaking",
          "Strategic support",
        ],
      },
    },
    stakeholders: {
      overline: "Our stakeholders",
      title: "On both sides of the bridge",
      leftTitle: "Africa side",
      rightTitle: "International side",
      left: [
        "Presidencies and governments",
        "Ministries",
        "Public administrations",
        "Government agencies",
        "State-owned enterprises",
        "Local authorities",
        "Public financial institutions",
        "Investment promotion agencies",
      ],
      right: [
        "Institutional investors",
        "Investment funds",
        "Banks",
        "Financial institutions",
        "Donor agencies",
        "Multinational corporations",
        "Industrial groups",
        "Specialised advisory firms",
        "Technology companies",
        "Development partners",
      ],
    },
    process: {
      overline: "Intelligence before intermediation",
      title: "A connection is only valuable if it is relevant.",
      text: "Every intermediation mandate rests on prior work of understanding, analysis and qualification. Bridge Intell assesses context, actors, stakes, risks and respective interests before arranging any introduction.",
      steps: [
        { name: "Understand", desc: "The need, the institutional context and each party's true objectives." },
        { name: "Analyse", desc: "Actors, stakes, risks, economic and regulatory environments." },
        { name: "Qualify", desc: "The strength, credibility and alignment of potential partners." },
        { name: "Connect", desc: "Arrange the introduction, at the right level, at the right time." },
        { name: "Support", desc: "Follow the relationship through to structuring and decision." },
      ],
    },
    sectors: {
      overline: "Strategic sectors",
      title: "Where structuring projects happen",
      text: "Fourteen priority sectors, from infrastructure to natural resources, where Bridge Intell mobilises its network and market intelligence.",
    },
    confidentiality: {
      overline: "Confidentiality",
      title: "Discretion is part of our craft.",
      text: "Institutional, financial and strategic projects require a high level of confidentiality. Bridge Intell applies an approach built on discretion, controlled information flow and protection of the interests of all parties involved.",
      points: [
        "No project, government, investor or introduction is ever made public without authorisation.",
        "Information circulates on a controlled, need-to-know basis, by agreement.",
        "Constant protection of the interests of the parties involved.",
      ],
      marquee: [
        "Africa and the world",
        "Governments and investors",
        "Projects and capital",
        "Needs and solutions",
        "Information and decision",
        "Opportunities and partners",
      ],
    },
    opportunities: {
      overline: "Opportunities",
      title: "A selection of qualified opportunities",
      text: "Infrastructure projects, investor searches, public-private partnerships: every published opportunity is analysed and qualified beforehand. Three visibility tiers protect sensitive information.",
      all: "All",
      public: "Public",
      confidential: "Confidential",
      private: "Strictly private",
      locked: "Confidential opportunity",
      lockedText: "Detailed information on this opportunity is available upon registration and qualification.",
      requestAccess: "Request access",
      profileSought: "Profile sought",
      amount: "Indicative amount",
      statusOpen: "Open",
      statusClosed: "Closed",
      privateNote: "Strictly private opportunities are never published. They are presented only to qualified parties, under confidentiality undertakings.",
    },
    contact: {
      overline: "Contact",
      title: "Open a gateway",
      text: "Present a project, explore an opportunity or request access to a confidential file. Every message is handled with discretion.",
      intentProject: "Present a project",
      intentOpportunity: "Explore an opportunity",
      intentAccess: "Request access to a file",
      roleAfrica: "African institution",
      roleWorld: "International actor",
      name: "Full name",
      organization: "Organisation",
      email: "Professional email",
      country: "Country",
      sector: "Relevant sector",
      message: "Your message",
      messagePlaceholder: "Briefly describe your project, your need or your interest…",
      submit: "Send in full confidentiality",
      sending: "Sending…",
      success: "Message delivered. Bridge Intell will respond with the utmost discretion.",
      error: "Something went wrong. Please try again.",
    },
    footer: {
      signature: "Bridging Africa to the World",
      alt1: "L'Afrique connectée aux opportunités du monde",
      alt2: "Le pont stratégique entre l'Afrique et le monde",
      rights: "All rights reserved.",
    },
    admin: {
      login: "Admin area",
      email: "Email",
      password: "Password",
      signIn: "Sign in",
      logout: "Sign out",
      messages: "Messages",
      opportunities: "Opportunities",
      sectors: "Sectors",
      newOpportunity: "New opportunity",
      newSector: "New sector",
      save: "Save",
      cancel: "Cancel",
      edit: "Edit",
      delete: "Delete",
      markRead: "Mark read",
      noMessages: "No messages yet.",
      visibility: "Visibility",
    },
  },
};

const LanguageContext = createContext(null);

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(() => localStorage.getItem("bi_lang") || "fr");
  const setLang = useCallback((l) => {
    localStorage.setItem("bi_lang", l);
    setLangState(l);
  }, []);
  const t = translations[lang];
  return (
    <LanguageContext.Provider value={{ lang, setLang, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLang() {
  return useContext(LanguageContext);
}
